import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { AutoCompleteDataSourceEnum } from 'src/app/shared/components/modals/auto-complete-modal/enums/auto-complete-data-source.enum';
import { pageInfoFragment } from 'src/app/shared/gql-fragments/gql-fragments';
import { PaginatedQueryPage } from 'src/app/shared/models/gql/special/paginated-query-page.model';
import { Remote } from 'src/app/shared/remote';

@Injectable({
  providedIn: 'root',
})
export class AutoCompleteService extends Remote {
  /**
   * This API performs search using designated data source.
   *
   * @param dataSource - Data Source that will be used for searching.
   * @param searchParameters - Search parameters object.
   * @param searchParameters.searchString - Search string.
   * @param searchParameters.fundingSourceCode - funding source code for partner orgs query.
   */
  async search<T extends AutoCompleteDataSourceEnum>(
    dataSource: T,
    searchParameters: { searchString: string; fundingSourceCode?: string },
  ): Promise<T extends AutoCompleteDataSourceEnum.PARTNER_FUNDING_ORGANISATION ? PaginatedQueryPage<any> : any> {
    switch (dataSource) {
      case AutoCompleteDataSourceEnum.SUBURB:
        return this.searchSuburb(searchParameters.searchString);
      case AutoCompleteDataSourceEnum.PARTNER_FUNDING_ORGANISATION:
        return this.searchPartnerFundingOrganisation(searchParameters.searchString, searchParameters.fundingSourceCode);
      default:
        throw new Error('Search Data Source not implemented.');
    }
  }

  /**
   * This API returns a list of matched suburbs.
   *
   * @param searchString - Possible values could be suburb, postcode etc.
   */
  private async searchSuburb(searchString: string): Promise<any> {
    try {
      // encodeURIComponent() function to replace the space with '%20'
      const url = `${this.api}/search/suburb_search/${encodeURIComponent(searchString)}`;

      const res = await this.apiCallRetry('GET', url);
      return res.body;
    } catch (e) {
      const error = e ? e.error : 'Could not fetch locations, please retry later.';
      return Promise.reject(error);
    }
  }

  /**
   * This API returns a list of matched Support at Home funding provider name.
   *
   * @param fundingSourceCode - funding source.
   * @param searchString - org name search string.
   */
  private async searchPartnerFundingOrganisation(
    searchString: string,
    fundingSourceCode = 'home_care_package',
  ): Promise<PaginatedQueryPage<any>> {
    try {
      const res = await this.baseAPI.gqlQuery(
        gql`
          query searchFundingOrgs($fundingSourceCode: String!, $query: String!) {
            searchFundingOrgs(fundingSourceCode: $fundingSourceCode, query: $query) {
              nodes {
                businessName
              }
              pageInfo {
                ...PageInfo
              }
            }
          }
          ${pageInfoFragment}
        `,
        {
          fundingSourceCode,
          query: searchString,
        },
      );

      return res['searchFundingOrgs']['nodes'];
    } catch (e) {
      return Promise.reject(e?.error);
    }
  }
}
